(() => {
  if (customElements.get('oilogic-tabs')) return;

class OilogicTabs extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    /* The markup groups tabs into presentation rows whose DOM order differs
       from the visual 1-2-3-4 sequence, so order by --tab-order and pair
       each tab with its panel through aria-controls. */
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]')).sort(
      (a, b) =>
        Number(a.style.getPropertyValue('--tab-order') || 0) -
        Number(b.style.getPropertyValue('--tab-order') || 0)
    );
    this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));
    if (this.tabs.length === 0) return;

    this.currentIndex = Math.max(
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'),
      0
    );

    const { signal } = this.controller;
    this.tabs.forEach((tab, index) => {
      tab.addEventListener(
        'click',
        () => {
          this.stopAutoplay();
          this.select(index);
        },
        { signal }
      );
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index), { signal });
    });

    this.setupAutoplay();
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    this.pauseAutoplay();
  }

  select(index) {
    this.currentIndex = index;
    /* FLIP: remember where each pill sits, apply the new state (rows may
       swap), then slide every displaced pill from its old spot. */
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const origins = reduceMotion ? null : new Map(this.tabs.map((tab) => [tab, tab.getBoundingClientRect()]));
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (this.panels[i]) this.panels[i].hidden = !active;
    });
    if (origins) {
      this.tabs.forEach((tab) => {
        const before = origins.get(tab);
        const after = tab.getBoundingClientRect();
        const dx = before.left - after.left;
        const dy = before.top - after.top;
        if (dx || dy) {
          tab.animate(
            [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
            { duration: 350, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
          );
        }
      });
    }

    /* Mobile strip: center the active pill in view. Horizontal only — a
       scrollIntoView here could yank the page's vertical scroll whenever
       autoplay rotates while the section is off-screen. */
    const list = this.tabs[index].closest('[role="tablist"]');
    if (list && list.scrollWidth > list.clientWidth) {
      const tab = this.tabs[index];
      const delta = tab.getBoundingClientRect().left - list.getBoundingClientRect().left;
      list.scrollTo({
        left: list.scrollLeft + delta - (list.clientWidth - tab.offsetWidth) / 2,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.stopAutoplay();
    this.select(next);
    this.tabs[next].focus();
  }

  /* Auto-rotation (WCAG 2.2.2): pauses on hover/focus, stops for good once
     the visitor picks a tab, and never runs under prefers-reduced-motion. */
  setupAutoplay() {
    this.autoplayDelay = Number(this.dataset.autoplay);
    if (!this.autoplayDelay || this.tabs.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { signal } = this.controller;
    this.addEventListener('pointerenter', () => this.pauseAutoplay(), { signal });
    this.addEventListener('pointerleave', () => this.resumeAutoplay(), { signal });
    this.addEventListener('focusin', () => this.pauseAutoplay(), { signal });
    this.addEventListener('focusout', () => this.resumeAutoplay(), { signal });
    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.pauseAutoplay() : this.resumeAutoplay()),
      { signal }
    );

    this.resumeAutoplay();
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resumeAutoplay() {
    if (this.autoplayStopped || !this.autoplayDelay || this.autoplayTimer) return;
    this.autoplayTimer = setInterval(
      () => this.select((this.currentIndex + 1) % this.tabs.length),
      this.autoplayDelay
    );
  }

  stopAutoplay() {
    this.autoplayStopped = true;
    this.pauseAutoplay();
  }
}

customElements.define('oilogic-tabs', OilogicTabs);
})();
